var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { call, flush, cancel, cancelled, select, fork, put, delay, takeEvery, take, } from "@redux-saga/core/effects";
import { publishState, publishStreamConnectionStateChange, publishStreamRequest, unpublishStreamRequest, } from "./actions";
import { publishStreamConnectionStateChangePattern, publishStreamRequestPattern, unpublishStreamRequestPattern, conferenceHoldPattern, } from "./patterns";
import { buffers, channel } from "redux-saga";
import { PublishStream } from "./index";
import { closeMediaStream } from "../../../Common";
import { clearAllStreamTasks } from "../../actions";
import { StreamSession, debugIceCandidateFilter } from "../../stream/";
import { iceServers, browser as getBrowser, stats } from "../../selectors";
import { conferenceHoldEvent } from "../actions";
import log from "../../../log";
var States;
(function (States) {
    States["CREATE"] = "CREATE";
    States["CONNECTING"] = "CONNECTING";
    States["CONNECTING_FAILED"] = "CONNECTING_FAILED";
    States["CONNECTED"] = "CONNECTED";
    States["DISCONNECTED"] = "DISCONNECTED";
    States["RECONNECTING"] = "RECONNECTING";
    States["RECONNECTING_FAILED"] = "RECONNECTING_FAILED";
    States["RECONNECTED"] = "RECONNECTED";
    States["CLOSING"] = "CLOSING";
})(States || (States = {}));
/**
 * Task manager manages lifecycle of children tasks and filters actions
 * @param context - context: ConferenceSessionImpl
 */
export function* publishStreamTaskManager(context) {
    const tasks = context.publishTasks;
    yield takeEvery([
        conferenceHoldPattern(context.conference.name),
        publishStreamRequestPattern(context.conference.name),
        unpublishStreamRequestPattern(tasks),
        publishStreamConnectionStateChangePattern(tasks),
        clearAllStreamTasks.toString(),
    ], function* (action) {
        try {
            switch (action.type) {
                case conferenceHoldEvent.toString(): {
                    Object.values(tasks).forEach(({ manager }) => {
                        if (manager && manager.context && manager.context.rtc) {
                            updateMediaState(manager.context);
                        }
                    });
                    break;
                }
                case publishStreamRequest.toString(): {
                    let manager = tasks[action.payload.streamId].manager;
                    // create new task if none exists, with internal channel to use
                    if (manager && !!manager.task && manager.task.isRunning()) {
                        log.debug(`publishStreamTaskManager: task with same streamId\
                         ${action.payload.streamId} running already. Ignoring.`);
                    }
                    else {
                        manager = tasks[action.payload.streamId].manager = {
                            task: null,
                            context: null,
                        };
                        manager.context = {
                            csi: context,
                            action,
                            ps: null,
                            rtc: null,
                            channel: yield call(channel, buffers.expanding()),
                            icechannel: yield call(channel, buffers.expanding()),
                        };
                        manager.task = yield fork(publishStreamTask, manager.context);
                    }
                    break;
                }
                case unpublishStreamRequest.toString(): {
                    yield put(tasks[action.payload.streamId].manager.context.channel, publishState({
                        next: States.CLOSING,
                    }));
                    break;
                }
                case publishStreamConnectionStateChange.toString(): {
                    // forward only the states we are interested in
                    // @ts-ignore
                    if (["connected", "disconnected", "failed"].includes(action.payload.state)) {
                        yield put(tasks[action.payload.streamId].manager.context.channel, action);
                    }
                    break;
                }
                case clearAllStreamTasks.toString(): {
                    // @ts-ignore
                    for (const [, { manager }] of Object.entries(tasks)) {
                        if (!!manager.task && manager.task.isRunning()) {
                            yield put(manager.context.channel, publishState({
                                next: States.CLOSING,
                            }));
                        }
                    }
                }
            }
        }
        catch (error) {
            log.warn("publishStreamTask error", error);
        }
        finally {
            if (yield cancelled()) {
                log.debug("publishStreamTask cancelled");
            }
        }
    });
}
export function* updateStreamMetadata(context, metadata) {
    yield call([context, context.updateStreamMetadata], metadata);
}
export function* updateStreamVideoBitrate(context, bitrate) {
    yield call([context, context.updateStreamVideoBitrate], bitrate);
}
function updateMediaState(context) {
    if (!context.rtc) {
        return;
    }
    const enabled = {
        audio: !context.ps.isMuted("audio"),
        video: !context.ps.isMuted("video"),
    };
    if (context.csi.isOnHold()) {
        enabled.audio = false;
        enabled.video = false;
    }
    context.rtc.setTrackKindEnabled("audio", enabled.audio);
    context.rtc.setTrackKindEnabled("video", enabled.video);
}
/**
 * Keeps a common context for all state sagas and listens for state change events to run asked state
 * @param csi
 * @param action
 * @param source - channel
 */
function* publishStreamTask(context) {
    context.status = {
        [States.CONNECTING]: {
            times: 0,
            max_retries: 15,
            retry_delay: 2000,
        },
        [States.RECONNECTING]: {
            times: 0,
            max_retries: 15,
            retry_delay: 2000,
        },
    };
    // initial state
    let state = publishState({ next: States.CREATE });
    try {
        while (state.payload) {
            log.debug("Executing state ", state.payload);
            switch (state.payload.next) {
                case States.CREATE: {
                    state = yield* createState(context);
                    break;
                }
                case States.CONNECTING: {
                    state = yield* connectingState(context);
                    break;
                }
                case States.DISCONNECTED: {
                    state = yield* disconnectedState(context, state);
                    break;
                }
                case States.CONNECTING_FAILED: {
                    state = yield* connectingFailedState(context);
                    break;
                }
                case States.CONNECTED: {
                    state = yield* connectedState(context);
                    break;
                }
                case States.RECONNECTING: {
                    state = yield* reconnectingState(context);
                    break;
                }
                case States.RECONNECTING_FAILED: {
                    state = yield* reconnectingFailedState(context);
                    break;
                }
                case States.RECONNECTED: {
                    state = yield* reconnectedState(context);
                    break;
                }
                case States.CLOSING: {
                    state = yield* closingState(context);
                    break;
                }
            }
        }
        yield cancel();
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("Unexpected error in publishStreamTask ", error);
        }
    }
    finally {
        // make sure closingState is called but only once
        if (!!state.payload && (yield cancelled())) {
            yield call(closingState, context);
        }
    }
}
/**
 * initial state, creates required classes and completes the promise
 * @param context
 */
function* createState(context) {
    context.ps = new PublishStream(context.csi, context.action);
    context.rtc = new StreamSession({
        fixRtpExtensionIds: true,
        statsList: yield select(stats),
    });
    context.ps.setRtc(context.rtc);
    context.ps.syncMuteState("audio");
    context.ps.syncMuteState("video");
    yield fork(sendPublishStreamIceCandidatesSaga, context);
    const manager = context.csi.publishTasks[context.action.payload.streamId];
    try {
        context.rtc.localMediaStream = manager.options.mediaStream;
        yield call([context.rtc, context.rtc.resolveMediaStream], manager.options.mediaConstraints);
        context.ps.mediaStream = context.rtc.localMediaStream;
        context.csi.sendEvent("streamAdded", context.ps);
        manager.promise.resolve(context.ps);
    }
    catch (error) {
        manager.promise.reject(context.ps);
        context.csi.sendEvent("streamFailed", context.ps);
        throw error;
    }
    finally {
        manager.promise = undefined;
    }
    return publishState({ next: States.CONNECTING });
}
function* initRtc(context) {
    const store = context.csi.getParentContext().getStore();
    yield call([context.rtc, context.rtc.create], {
        iceServers: yield select(iceServers),
    });
    context.rtc.on("icecandidate", (event) => context.icechannel.put({
        candidate: event.candidate,
    }));
    context.rtc.on("connectionstatechange", () => store.dispatch(publishStreamConnectionStateChange({
        streamId: context.ps.streamId,
        state: context.rtc.pc.connectionState,
    })));
    if (context.csi.statisticsOptions && context.csi.statisticsOptions.enable) {
        let counter = 0;
        context.rtc.on("stats", () => __awaiter(this, void 0, void 0, function* () {
            try {
                if (++counter % 9 === 0) {
                    // once is enough for browser info
                    const data = counter === 9
                        ? Object.assign({ browser: getBrowser(context.csi.getParentContext().getStore().getState()) }, (yield context.rtc.getAllLogs())) : yield context.rtc.getLastLogs();
                    yield context.csi
                        .getParentContext()
                        .getStatsResource()
                        .update(Object.assign({ sessionId: context.csi.conferenceId, userId: context.ps.userId, userEndpointId: context.ps.userEndpointId, role: "outgoing", type: "conference" }, data), { urlPostfix: `streams/${context.rtc.id}` });
                }
            }
            catch (error) {
                log.debug("on upload stats", error);
            }
        }));
        context.rtc.enableStats(context.csi.statisticsOptions.interval);
    }
}
/**
 * Tries to connect and then watches for what actually happened
 * @param context
 */
function* connectingState(context) {
    try {
        yield* initRtc(context);
        context.rtc.fillMedia(context.rtc.localMediaStream);
        context.rtc.setDirection(["audio", "video"], true, false);
        updateMediaState(context);
        yield call([context.rtc, context.rtc.offer]);
        yield call([context.ps, context.ps.requestAnswer], context.rtc.pc.localDescription.sdp);
        yield call([context.rtc, context.rtc.setSdp], {
            type: "answer",
            sdp: context.ps.response.sdpAnswer,
        });
        while (true) {
            const event = yield take(context.channel);
            if (event.type === publishStreamConnectionStateChange.toString()) {
                if (event.payload.state === "connected") {
                    return publishState({ next: States.CONNECTED });
                }
                else if (event.payload.state === "disconnected") {
                    return publishState({
                        next: States.DISCONNECTED,
                        previous: States.CONNECTING,
                    });
                }
                else if (event.payload.state === "failed") {
                    return publishState({ next: States.CONNECTING_FAILED });
                }
            }
            else if (event.type === publishState.toString()) {
                // forward state change
                return event;
            }
        }
    }
    catch (error) {
        // if not cancelled dispatch recover action
        if (!(yield cancelled())) {
            log.error("Error in connectingState ", error);
            // ignore 404
            if (error.response && error.response.status === 404) {
                return publishState({ next: States.CLOSING });
            }
            else {
                return publishState({ next: States.CONNECTING_FAILED });
            }
        }
    }
    finally {
        context.status[States.CONNECTING].times++;
    }
}
/**
 * Sends client warning event and waits for connection to disconnect or fail
 * @param context
 * @param action
 */
function* disconnectedState(context, action) {
    const status = context.status[action.payload.previous];
    if (status.times <= 1) {
        context.csi.sendEvent("streamWarning", context.ps);
    }
    try {
        while (true) {
            const event = yield take(context.channel);
            if (event.type === publishStreamConnectionStateChange.toString()) {
                if (event.payload.state === "connected") {
                    return publishState({
                        next: action.payload.previous === States.CONNECTING
                            ? States.CONNECTED
                            : States.RECONNECTED,
                    });
                }
                else if (event.payload.state === "failed") {
                    return publishState({
                        next: action.payload.previous === States.CONNECTING
                            ? States.CONNECTING_FAILED
                            : States.RECONNECTING_FAILED,
                    });
                }
            }
            else if (event.type === publishState.toString()) {
                return event;
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("disconnectedState got unexpected error ", error);
            return publishState({ next: States.CLOSING });
        }
    }
}
/**
 * Decides if should retry connecting after delay or give up
 * @param context
 */
function* connectingFailedState(context) {
    const connecting = context.status[States.CONNECTING];
    if (connecting.times > connecting.max_retries) {
        context.csi.sendEvent("streamFailed", context.ps);
        return publishState({ next: States.CLOSING });
    }
    else {
        if (!context.csi.isClosing()) {
            yield call([context.ps, context.ps.stopStream]);
        }
        yield delay(connecting.retry_delay);
        return publishState({ next: States.CONNECTING });
    }
}
/**
 * Notifies client of streamAdded and waits for cancellation or connection to fail/disconnect
 * @param context
 */
function* connectedState(context) {
    // reset connecting trial times
    context.status[States.CONNECTING].times = 0;
    try {
        context.ps.mediaStream = context.rtc.localMediaStream;
        context.csi.sendEvent("streamPublished", context.ps);
        while (true) {
            const event = yield take(context.channel);
            if (event.type === publishStreamConnectionStateChange.toString() &&
                (event.payload.state === "disconnected" ||
                    event.payload.state === "failed")) {
                return publishState({
                    next: States.DISCONNECTED,
                    previous: States.RECONNECTING, // force reconnection flow
                });
            }
            else if (event.type === publishState.toString()) {
                return event;
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("connectedState got unexpected error ", error);
            return publishState({ next: States.CLOSING });
        }
    }
}
/**
 * Tries to reconnect and then watches for what actually happened
 * @param context
 */
function* reconnectingState(context) {
    try {
        yield* initRtc(context);
        context.rtc.fillMedia(context.rtc.localMediaStream);
        context.rtc.setDirection(["audio", "video"], true, false);
        updateMediaState(context);
        yield call([context.rtc, context.rtc.offer]);
        yield call([context.ps, context.ps.requestAnswer], context.rtc.pc.localDescription.sdp);
        yield call([context.rtc, context.rtc.setSdp], {
            type: "answer",
            sdp: context.ps.response.sdpAnswer,
        });
        while (true) {
            const event = yield take(context.channel);
            if (event.type === publishStreamConnectionStateChange.toString()) {
                if (event.payload.state === "connected") {
                    return publishState({ next: States.RECONNECTED });
                }
                else if (event.payload.state === "disconnected") {
                    return publishState({
                        next: States.DISCONNECTED,
                        previous: States.RECONNECTING,
                    });
                }
                else if (event.payload.state === "failed") {
                    return publishState({ next: States.RECONNECTING_FAILED });
                }
            }
            else if (event.type === publishState.toString()) {
                return event;
            }
        }
    }
    catch (error) {
        // if not cancelled dispatch recover action
        if (!(yield cancelled())) {
            log.error("Error in reconnectingState ", error);
            // ignore 404
            if (error.response && error.response.status === 404) {
                return publishState({ next: States.CLOSING });
            }
            else {
                return publishState({ next: States.RECONNECTING_FAILED });
            }
        }
    }
    finally {
        context.status[States.RECONNECTING].times++;
    }
}
/**
 * Decides if should retry connecting after delay or give up
 * @param context
 */
function* reconnectingFailedState(context) {
    const reconnecting = context.status[States.RECONNECTING];
    if (reconnecting.times > reconnecting.max_retries) {
        context.csi.sendEvent("streamFailed", context.ps);
        return publishState({ next: States.CLOSING });
    }
    else {
        if (!context.csi.isClosing()) {
            yield call([context.ps, context.ps.stopStream]);
        }
        yield delay(reconnecting.retry_delay);
        return publishState({ next: States.RECONNECTING });
    }
}
/**
 * Notifies client of streamRecovery and waits for cancellation or connection to fail/disconnect
 * @param context
 */
function* reconnectedState(context) {
    // reset reconnecting trial times
    context.status[States.RECONNECTING].times = 0;
    try {
        context.ps.mediaStream = context.rtc.localMediaStream;
        context.csi.sendEvent("streamRecovered", context.ps);
        while (true) {
            const event = yield take(context.channel);
            if (event.type === publishStreamConnectionStateChange.toString() &&
                (event.payload.state === "disconnected" ||
                    event.payload.state === "failed")) {
                yield put(context.channel, publishState({
                    next: States.DISCONNECTED,
                    previous: States.RECONNECTING,
                }));
            }
            else if (event.type === publishState.toString()) {
                return event;
            }
        }
    }
    catch (error) {
        if (!(yield cancelled())) {
            log.error("reconnectedState got unexpected error ", error);
            return publishState({ next: States.CLOSING });
        }
    }
}
function uploadFinalStats(context, callback) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (context.csi.statisticsOptions && context.csi.statisticsOptions.enable) {
                const logs = yield context.rtc.getAllLogs();
                yield context.csi
                    .getParentContext()
                    .getStatsResource()
                    .update(Object.assign({ sessionId: context.ps.conferenceId, userId: context.ps.userId, userEndpointId: context.ps.userEndpointId, role: "outgoing", type: "conference", browser: getBrowser(context.csi.getParentContext().getStore().getState()) }, logs), { urlPostfix: `streams/${context.rtc.id}` });
            }
        }
        catch (error) {
            log.debug("on upload final stats", error);
        }
        finally {
            callback();
        }
    });
}
/**
 * cleanup routine executed by request or when main task cancelled
 * @param context
 */
function* closingState(context) {
    context.csi.sendEvent("streamRemoved", context.ps);
    if (!context.csi.isClosing()) {
        yield call([context.ps, context.ps.stopStream]);
    }
    context.rtc.close();
    const manager = context.csi.publishTasks[context.action.payload.streamId];
    if (context.rtc.localMediaStream && !manager.options.mediaStream) {
        closeMediaStream(context.rtc.localMediaStream);
    }
    uploadFinalStats(context, () => (context.rtc = null));
    return publishState(null);
}
/**
 * Either side gathers ice candidates and sends them to server in bulk
 * @param context
 * @param action
 */
function* sendPublishStreamIceCandidatesSaga(context) {
    let startTime = null;
    let candidates = null;
    let numberOfCandidates = 0;
    try {
        while (true) {
            // trigger candidates epoch
            if (candidates === null) {
                candidates = debugIceCandidateFilter([yield take(context.icechannel)]);
                startTime = Date.now();
            }
            yield delay(250);
            candidates = candidates.concat(debugIceCandidateFilter(yield flush(context.icechannel)));
            if (candidates.length && context.ps.answerReceived) {
                const iceCandidates = candidates
                    .filter(({ candidate }) => !!candidate)
                    .map(({ candidate }) => candidate.toJSON());
                if (iceCandidates.length) {
                    numberOfCandidates += iceCandidates.length;
                    yield fork([context.ps, context.ps.addCandidateRequest], iceCandidates);
                }
                if (candidates.length !== iceCandidates.length) {
                    // we reached the end of candidates because the condition above is only true
                    // when there's a null candidate received
                    log.debug(`call ice candidate sending stops now after ${Date.now() - startTime}` + ` millis and ${numberOfCandidates} candidates found`);
                    candidates = null;
                    numberOfCandidates = 0;
                }
                else {
                    candidates = [];
                }
            }
        }
    }
    catch (error) {
        log.debug("sendPublishStreamIceCandidatesSaga probably was cancelled");
    }
}
